import * as constants from "../core/constants.js";
import clone from "../core/util/clone.js";
import * as shapeToGridConverterMaker from "../core/geometry/shape-to-grid-converter.js";
import * as shapeSpatialRelations from "../core/geometry/shape-spatial-relations.js";
import * as random from "../core/util/random.js";
import * as coreFunctions from "../core/core-functions.js";
import * as gameStateFunctions from "../core/game-state-functions.js";
import * as trajectoryUtil from "../core/geometry/trajectory/trajectory-util.js";
import * as boardModel from "./board-model.js";
import { getGridBlocks, BLOCK_SHIFT } from "../core/util/grid-blocks.js";
import { crowdSettingsFor } from "./crowd.js";
import { powerUpGreedValue } from "./powerup-values.js";
import { CellStamps } from "../core/util/cell-stamps.js";
import { stadiumMirror } from "../core/power-up/effect-definitions/wall-hack.js";

var RoundingModes = shapeToGridConverterMaker.RoundingModes;
var shapeToGridConverter = shapeToGridConverterMaker.createShapeToGridConverter();

var SIMULATION_DURATION = 2;
var SIMULATION_DELTA = 0.05;
var SEARCH_DIRECTION_VELOCITY = 7*Math.PI;
var CAUTION_CLEARANCE_REF = 160; // units: clearance at/above this feels fully "open"
var TRAIT_CLAMP = 0.5;           // max |trait tilt| on survival time
var AGGRESSION_RADIUS = 70;      // units: how close to an opponent's projected path a sample must pass to count as a cut
var CUT_MARGIN = 0.3;            // s: we must reach the crossing this much before them for a full-strength cut
// Head proximity is a BAND, not a magnet: nothing within HEAD_TOO_CLOSE (that
// is a collision waiting to happen), full reward between HEAD_NEAR and HEAD_FAR,
// fading to nothing at HEAD_RADIUS. A full cut is worth 0.5 on top, so a real
// cut-off (with its 0.3s lead) still outranks hanging around.
var HEAD_TOO_CLOSE = 40;         // units
var HEAD_NEAR = 100;             // units: full reward from here ...
var HEAD_FAR = 200;              // units: ... to here
var HEAD_RADIUS = 400;           // units: no reward beyond
var HEAD_WEIGHT = 0.3;           // bonus in the band when AHEAD of their heading (half that behind it)
var HEAD_LEAD = 0;               // s: 0 = the band is centred on their projected head at our sample time; anchoring it on a point in front of them (0.25 and 0.5 tried) put hunters where opponents drive and cost more deaths than it earned kills
var CUT_WEIGHT = 0.7;            // a full cut-off (trail across their near future) is worth this much: more than anything else
var GREED_RADIUS = 90;           // units: token attraction range (a sample this close to a power-up is drawn to it)
var EXTRA_SURVIVAL_WEIGHT = 1.0; // survival beyond the safe threshold counts at full weight: a longer-surviving safe path beats most personality differences
// Skilled bots compare a FAN of turn candidates (turn by this angle, then
// straight) on top of the random and angle-scan ones, so a trait has paths on
// the menu that can express it. Bots with traits also re-evaluate the menu
// while their path is still safe, every REEVAL_INTERVAL, and switch only when
// a candidate beats the current plan by REEVAL_MARGIN (in trait-average
// terms) so steering stays calm.
var FAN_ANGLES = [0, -0.8, 0.8, -1.6, 1.6, -0.35, 0.35, -2.4, 2.4]; // rad, in priority order: a low breadth gets the first few
// Extra fan angles a level may add on top of a bot's own breadth (crowd
// fanExtra: the 1v1 level has CPU to spare), in priority order: the gaps
// between the regular angles first, then the sharpest turns.
var FAN_EXTRA_ANGLES = [-1.2, 1.2, -0.55, 0.55, -2.0, 2.0, -0.15, 0.15, -2.8, 2.8, -1.0, 1.0, -1.4, 1.4];
var REEVAL_INTERVAL = 0.25; // s
var REEVAL_MARGIN = 0.06;   // trait-average units (score margin = safeTime * this)
// Roster-dependent knobs (fan size, planning cadence, lookahead, per-tick
// budgets, personalities, opponent prediction, the own room map) all come
// from the crowd ladder (crowd.js): one table, shared with the board model
// and the host loop, so small games keep the full planner untouched.
var EMERGENCY_FAN = 2;
// Candidates scoring within NEAR_TIE (x safeTime) of the best are a toss-up:
// one is picked at random (seeded) instead of always the first. In open space
// many turns survive the whole lookahead, so this is what keeps the bots
// unpredictable; the single-best rule made every escape the same gentle arc.
var NEAR_TIE = 0.35; // a level may set its own (crowd.js nearTie)
// The planner's wall hack: a worm through the wall comes back in on the far
// side (wall-hack.js), and the hack runs out. Off = the old blind spot (paths
// out into the void past the wall read as free), for comparisons in the lab.
// Set per bot at the top of each update (planning is synchronous).
var wallHackWrap = true;

// Opponent threat model for the survival check. An opponent's projected trail
// (board model, current steering held) is treated as a PROBABLE obstacle: it
// is certain right in front of them and fades to nothing at the horizon,
// because the further out the projection, the likelier they steer elsewhere.
var THREAT_CERTAIN_TIME = 0.2; // s: a projection this fresh is taken as certain
var THREAT_HORIZON = 2;        // s: beyond this a projection is ignored entirely
// A bot plans against its NEAREST opponents only: the ones whose projected
// trail can touch its own lookahead at all (within reach: our lookahead plus
// their projection horizon, at worm speed, with a margin), capped at
// NEIGHBOUR_MAX. Every candidate's collision steps and trait samples loop over
// these, so without the cap a 40-worm box costs each bot ~13x a duel per
// replan and the host falls behind real time. A worm outside reach cannot
// meet us within the horizon, so the cap changes no decision that matters.
var NEIGHBOUR_MAX = 8;
var NEIGHBOUR_REACH_MARGIN = 1.15;
// A projected path's largest step between points, slightly inflated so rounding can
// only make a skip-ahead shorter (0 = unknown: no skipping).
function skipGap(path) {
    return path.maxGap > 0 ? path.maxGap * (1 + 1e-9) + 1e-9 : 0;
}

function threatProbability(t) {
    if (t <= THREAT_CERTAIN_TIME) {return 1;}
    if (t >= THREAT_HORIZON) {return 0;}
    return 1 - (t - THREAT_CERTAIN_TIME) / (THREAT_HORIZON - THREAT_CERTAIN_TIME);
}

// The three "how good" traits, each 0..1 (see docs/design/bots/PERSONALITIES.md):
//  - planning:  how far ahead it thinks (DEPTH). Lookahead 1.8..2.7s.
//  - breadth:   how many turn options it weighs (WIDTH): 3..9 candidates from
//               FAN_ANGLES, in that list's priority order, so a narrow bot
//               still has straight on and a gentle turn each way but never
//               finds the hard turn that would have saved it. The strongest
//               survival lever there is (crowd.js measured 9 vs 4 at +1.5s
//               alive). Absent = 1: the full fan, what every bot had before
//               breadth was its own trait, so existing bots play unchanged.
//  - precision: how well it executes the plan. Reaction time (planning
//               interval) 75..50ms, and a steering delay: each steering change
//               is applied after a random delay of up to 100..0ms that the
//               planner knows nothing about (an imprecise worm overshoots).
// A legacy `skill` (1..10) sets both to the same level; the traits override it.
// `undefined` skill and no traits
// returns the original constants verbatim (offline play, old tests).
var PLANNING_LOOKAHEAD = [1.8, 2.7];      // s
var SAFE_FRACTION = 0.75;                 // of the lookahead: a path surviving this much is safe (no replan; traits rank it). Same for everyone.
var BREADTH_FAN = [3, 9];                 // candidates in the fan, by the breadth trait (absent = 1 = all 9)
var PRECISION_REACTION = [0.075, 0.05];   // s: planning interval
var PRECISION_STEER_DELAY = [0.1, 0];     // s: max random delay on a steering change
// Reversed keys (the key_switch power-up flips a worm's turning direction). The
// planner reads the flipped value straight from the game state, so without this
// a bot adapts in zero time and the power-up only ever hurts people. Instead,
// when the turning direction flips, the bot's FINGERS stay wrong for a moment:
// it keeps planning correctly but presses the opposite key, like a person does.
// How long is precision (how well a bot executes): this much at precision 0 down
// to this much at 1 when the keys get reversed, and CONFUSION_BACK of that when
// they flip back (you half expect that one).
var PRECISION_CONFUSION = [0.6, 0.15];    // s (0.15, not less: even the best bot should visibly stumble)
var CONFUSION_BACK = 0.5;

// How long a flip of the turning direction confuses a bot of this precision.
// `reversedNow`: the keys just became reversed (true) or just went back to
// normal (false). Exported for the debug UIs and the tests.
export function confusionTime(precision, reversedNow) {
    var t = lerp(PRECISION_CONFUSION, clamp01(precision));
    return reversedNow ? t : t * CONFUSION_BACK;
}

// Track the worm's turning direction from tick to tick and say whether the bot
// is confused right now. Mutates aiData (turnSign, confusedUntil). The first
// tick only records the direction: a round that starts reversed confuses nobody.
export function updateConfusion(aiData, turningSpeed, gameTime, precision) {
    var sign = turningSpeed < 0 ? -1 : 1;
    if (aiData.turnSign !== undefined && aiData.turnSign !== sign) {
        aiData.confusedUntil = gameTime + confusionTime(precision, sign < 0);
    }
    aiData.turnSign = sign;
    return aiData.confusedUntil !== undefined && gameTime < aiData.confusedUntil;
}

// Every global knob that shapes a skilled bot's play, for debug UIs (the bots
// playground shows these so nothing that affects a worm is hidden).
export function getEngineInfo() {
    return {
        simulationDelta: SIMULATION_DELTA,              // s per collision step
        searchDirectionVelocity: SEARCH_DIRECTION_VELOCITY, // rad/s the angle-scan candidate sweeps
        threatCertainTime: THREAT_CERTAIN_TIME,
        threatHorizon: THREAT_HORIZON,
        cautionClearanceRef: CAUTION_CLEARANCE_REF, spaceCap: boardModel.SPACE_CAP_TILES, greedRadius: GREED_RADIUS,
        aggressionRadius: AGGRESSION_RADIUS, cutMargin: CUT_MARGIN, headTooClose: HEAD_TOO_CLOSE, headNear: HEAD_NEAR, headFar: HEAD_FAR, headRadius: HEAD_RADIUS, headWeight: HEAD_WEIGHT, headLead: HEAD_LEAD, cutWeight: CUT_WEIGHT,
        traitClamp: TRAIT_CLAMP,
        boardTiles: boardModel.TILES,
        boardRefreshInterval: boardModel.REFRESH_INTERVAL,
        pathStep: boardModel.PATH_STEP,
        pathHorizon: boardModel.PATH_HORIZON,
        candidatesPerReplan: 2 + FAN_ANGLES.length, // random turn + angle-scan turn + full fan (+ directed ones when live)
        fanAngles: FAN_ANGLES.slice(),
        planningLookahead: PLANNING_LOOKAHEAD.slice(), safeFraction: SAFE_FRACTION, breadthFan: BREADTH_FAN.slice(),
        precisionReaction: PRECISION_REACTION.slice(), precisionSteerDelay: PRECISION_STEER_DELAY.slice(),
        precisionConfusion: PRECISION_CONFUSION.slice(), confusionBack: CONFUSION_BACK,
        reevalInterval: REEVAL_INTERVAL,
        reevalMargin: REEVAL_MARGIN,
        extraSurvivalWeight: EXTRA_SURVIVAL_WEIGHT,
    };
}

function lerp(range, t) { return range[0] + (range[1] - range[0]) * t; }
function clamp01(v) { return v < 0 ? 0 : v > 1 ? 1 : v; }
var DEFAULT_LEVEL = 0.7; // planning / precision when a bot has traits but no levels and no skill (matches the playground default)
export function skillParams(skill, traits) {
    // Legacy constants ONLY for a bot with neither a skill nor any traits (the
    // original offline / test planner, evaluated every frame). Anything with
    // traits is a modern bot and needs the full parameter set.
    if ((skill === undefined || skill === null) && !traits) {
        return { simDuration: SIMULATION_DURATION, replanFraction: SAFE_FRACTION, fanCount: FAN_ANGLES.length, steerDelay: 0 };
    }
    var t = skill === undefined || skill === null ? DEFAULT_LEVEL : (Math.max(1, Math.min(10, skill)) - 1) / 9; // 0 at skill 1 .. 1 at skill 10
    var planning = clamp01(traits && traits.planning !== undefined ? traits.planning : t);
    var precision = clamp01(traits && traits.precision !== undefined ? traits.precision : t);
    var breadth = clamp01(traits && traits.breadth !== undefined && traits.breadth !== null ? traits.breadth : 1);
    return {
        planning: planning,
        precision: precision,
        breadth: breadth,
        simDuration: lerp(PLANNING_LOOKAHEAD, planning),
        replanFraction: SAFE_FRACTION,
        fanCount: Math.round(lerp(BREADTH_FAN, breadth)),
        // Planning cadence (reaction time): the expensive evaluate/replan runs at
        // this interval instead of every frame. Steering still follows the
        // current plan every frame, so movement stays smooth.
        replanInterval: lerp(PRECISION_REACTION, precision),
        steerDelay: lerp(PRECISION_STEER_DELAY, precision),
    };
}

// Cheap profile for decorative previews (lobby cards). Much shorter lookahead,
// coarser collision stepping, a single candidate path (no angle scan), and the
// expensive evaluate/replan throttled to ~20Hz instead of every frame. Worms
// stay lively (and crash a bit more), at a small fraction of the AI cost. Not
// used by real matches, so difficulty/determinism there are untouched.
function previewParams() {
    return {
        simDuration: 0.9,      // 0.9s lookahead (vs up to 2s): sees danger sooner
        simDelta: 0.1,         // ~9 collision steps per check (vs ~40)
        replanFraction: 0.7,   // bail on a doomed path eagerly (fewer crashes)
        replanInterval: 0.05,  // evaluate/replan at ~20Hz
        steerDelay: 0,
        angleScan: true,       // include the angle-scan escape (gets out of tight spots)
    };
}

// A stable 0..1 phase from a player id (which may be a string like "pv3"), used
// to stagger cheap-AI evaluations across bots. Must stay finite: a NaN here would
// poison the replan accumulator and stop the AI from ever planning.
function idPhase(id) {
    var s = String(id);
    var h = 0;
    for (var i = 0; i < s.length; i++) {
        h = (h * 31 + s.charCodeAt(i)) | 0;
    }
    return (Math.abs(h) % 1000) / 1000;
}

export default function PathCheckerAI() {

    function update(gameState, deltaTime, player, budget) {
        var aiData = player.aiData;
        var cheap = aiData.cheap === true;
        var params = cheap ? previewParams() : skillParams(aiData.skill, aiData.traits);
        var crowd = crowdSettingsFor(gameState);
        // A bot may carry its own settings over its level's (the duel lab pits
        // a boosted bot against an unboosted one in the same game).
        if (aiData.crowdOverride) { crowd = Object.assign({}, crowd, aiData.crowdOverride); }
        wallHackWrap = crowd.wallHackWrap !== false;
        if (!cheap) {
            params.replanInterval *= crowd.replanScale;
            params.simDuration *= crowd.lookaheadScale;
        }
        // "Skilled" = the modern planner (board model, fan, threat model): a
        // skill, or any traits at all. Only the legacy no-skill, no-trait path
        // (offline defaults, old tests) evaluates every frame against the grid.
        var skilled = aiData.skill !== undefined || !!aiData.traits;
        var simDuration = params.simDuration;
        var simDelta = (params.simDelta || SIMULATION_DELTA) * (cheap ? 1 : (crowd.simDeltaScale || 1));
        var frameDelta = deltaTime;

        // Skilled bots (and cheap previews) plan on a throttled cadence; only the
        // legacy no-skill path (offline defaults, old tests) evaluates every frame.
        var throttled = cheap || skilled;
        if (aiData.trajectory === undefined) {
            aiData.trajectory = [];
            aiData.searchDirection = 0;
            // Stagger throttled evaluations across bots (phase from the id) so a
            // full box doesn't replan on the same frame and spike.
            aiData.replanAccum = throttled ? idPhase(player.id) * params.replanInterval : 0;
        }

        var aliveWorms = gameStateFunctions.getAliveWorms(gameState, player.id);
        if (aliveWorms.length === 0) {
            return;
        }
        var worm = aliveWorms[0];

        // Tron turn (instant 90 degree turns, no arcs): when the effect starts or
        // ends the current plan is built from the wrong kind of motion, so drop
        // it and replan on the next tick with the right candidates.
        var tron = gameStateFunctions.hasWormEffect(gameState, worm.id, "tronTurn");
        if (aiData.tron !== tron) {
            aiData.tron = tron;
            aiData.trajectory = [];
            aiData.pendingSteering = null;
        }

        aiData.searchDirection += deltaTime * SEARCH_DIRECTION_VELOCITY;
        while (aiData.searchDirection > Math.PI) {
            aiData.searchDirection -= 2 * Math.PI;
        }

        // Trim the old trajectory (every frame, so following stays smooth)
        while (aiData.trajectory.length > 0 && deltaTime >= aiData.trajectory[0].duration) {
            deltaTime -= aiData.trajectory[0].duration;
            aiData.trajectory.shift();
        }
        if (aiData.trajectory.length > 0) {
            aiData.trajectory[0].duration -= deltaTime;
        }

        // Throttled mode: the expensive evaluate/replan (the trajectory
        // simulations) runs at params.replanInterval; other frames just follow
        // the current path. Legacy no-skill mode evaluates every frame.
        var doEvaluate = true;
        if (throttled) {
            aiData.replanAccum += frameDelta;
            doEvaluate = aiData.replanAccum >= params.replanInterval;
            if (doEvaluate) {
                aiData.replanAccum = 0;
            }
        }

        if (doEvaluate) {
            // Refresh the shared board model at the planning cadence (a no-op when
            // still fresh); trait scoring (see docs/design/bots/PERSONALITIES.md)
            // reads it. The legacy no-skill path skips it entirely.
            if (!cheap && skilled) {
                aiData.board = boardModel.getBoardModel(gameState);
            }

            // Personality scoring (docs/design/bots/PERSONALITIES.md): candidates are
            // COMPARED by survival time + weighted trait features sampled along the
            // path, but the replan TRIGGER stays pure survival (a stylish path that
            // is about to kill us still demands a replan). No traits or no board
            // model = scorer is null and everything behaves exactly as before.
            // The opponents that can matter to this replan (see NEIGHBOUR_MAX).
            var near = aiData.board ? nearbyOpponents(aiData.board, worm, (simDuration + THREAT_HORIZON) * (coreFunctions.getWormSpeed(gameState, worm.id) || 100) * NEIGHBOUR_REACH_MARGIN) : null;
            var scorer = crowd.personalities ? makeScorer(gameState, aiData, worm, near, crowd.ownField, crowd) : null;
            // Opponents as moving obstacles (skilled bots with a board model
            // only; legacy and cheap paths keep the static-grid check).
            var threats = crowd.threats ? makeThreats(aiData, worm, near) : null;
            // The "this path is fine" threshold: the replan trigger, and the
            // survival cutoff above which traits rank candidates.
            var safeTime = params.replanFraction * simDuration;

            var oldEval = evaluateTrajectory(gameState, worm, aiData.trajectory, simDelta, scorer, threats, safeTime);
            var doomed = oldEval.time < safeTime;
            // Periodic re-evaluation while safe (skilled bots with traits only):
            // without it a bot keeps whatever path first survived, and its
            // personality never gets a say until it is in danger again.
            var reeval = false;
            if (scorer && !cheap) {
                aiData.reevalAccum = (aiData.reevalAccum || 0) + params.replanInterval;
                if (aiData.reevalAccum >= REEVAL_INTERVAL) {
                    // Over budget this tick: keep the accumulator so it fires on the
                    // next evaluation instead (a doomed path ignores the budget).
                    if (!doomed && budget && budget.reevals >= crowd.maxReevalsPerTick) {
                        reeval = false;
                    } else {
                        aiData.reevalAccum = 0;
                        reeval = true;
                        if (!doomed && budget) {budget.reevals++;}
                    }
                }
            }
            // Don't update trajectory if the old one is fine, or the movement will be jerky
            if (doomed || reeval) {
                // The full menu, or (a busy tick) the emergency one.
                var full = !budget || budget.replans < crowd.maxFullReplansPerTick;
                if (full && budget) {budget.replans++;}
                var best = oldEval;
                var bestTrajectory = aiData.trajectory;
                var pool = []; // every evaluated candidate: {ev, trajectory}
                var consider = function (trajectory) {
                    var ev = evaluateTrajectory(gameState, worm, trajectory, simDelta, scorer, threats, safeTime);
                    pool.push({ ev: ev, trajectory: trajectory });
                    if (ev.score > best.score) {
                        best = ev;
                        bestTrajectory = trajectory;
                    }
                };
                if (tron) {
                    // The three moves a tron worm has: straight, right, left.
                    var tronTrajectories = generateTronTurnTrajectories(gameState, worm, simDuration);
                    for (var tti = 0; tti < tronTrajectories.length; tti++) {consider(tronTrajectories[tti]);}
                }
                // Random candidate first: it wins ties against the scan (strict >),
                // which keeps the bot less predictable.
                if (!tron) {consider(getRandomTrajectory(gameState, worm, simDuration));}
                // Use angle scan to get out of tricky situations. Cheap previews opt
                // in via params.angleScan (the main "smarts" lever).
                if (!tron && (!cheap || params.angleScan)) {
                    consider(getTrajectoryWithAngle(gameState, worm, aiData.searchDirection, simDuration));
                }
                // The fan (skilled bots): the menu a personality can pick from.
                if (!tron && !cheap && skilled) {
                    var fanN = Math.min(FAN_ANGLES.length, params.fanCount || FAN_ANGLES.length, crowd.fan);
                    if (!full) {fanN = Math.min(fanN, EMERGENCY_FAN);}
                    for (var fi = 0; fi < fanN; fi++) {
                        consider(getTrajectoryWithAngle(gameState, worm, FAN_ANGLES[fi], simDuration));
                    }
                    // The level's extra angles (fanExtra, 0 on most levels), full replans only.
                    var extraN = full ? Math.min(FAN_EXTRA_ANGLES.length, crowd.fanExtra || 0) : 0;
                    for (var xi = 0; xi < extraN; xi++) {
                        consider(getTrajectoryWithAngle(gameState, worm, FAN_EXTRA_ANGLES[xi], simDuration));
                    }
                }
                // The directed cut-off candidate (aggressive/hunting personas):
                // aimed at an opponent's projection; the trait scoring decides
                // whether it beats the safer options.
                if (!tron && full && scorer && scorer.targetAngle !== undefined) {
                    consider(getTrajectoryWithAngle(gameState, worm, scorer.targetAngle, simDuration));
                }
                // The token-directed candidate (greedy personas): aimed at the most
                // attractive power-up.
                if (!tron && full && scorer && scorer.tokenAngle !== undefined) {
                    consider(getTrajectoryWithAngle(gameState, worm, scorer.tokenAngle, simDuration));
                }
                // Near-ties with the best are a toss-up: pick one at random.
                if (skilled && pool.length > 1) {
                    var ties = [];
                    for (var pi2 = 0; pi2 < pool.length; pi2++) {if (pool[pi2].ev.score >= best.score - safeTime * (crowd.nearTie !== undefined ? crowd.nearTie : NEAR_TIE)) {ties.push(pool[pi2]);}}
                    if (ties.length > 1) {
                        var pick = ties[Math.floor(random.random(gameState) * ties.length)];
                        best = pick.ev;
                        bestTrajectory = pick.trajectory;
                    }
                }
                if (bestTrajectory !== aiData.trajectory) {
                    // Doomed: take the pick. Safe re-evaluation: only switch for a
                    // clear win over the current plan (hysteresis), never for noise.
                    if (doomed || best.score > oldEval.score + safeTime * REEVAL_MARGIN) {
                        aiData.trajectory = bestTrajectory;
                    }
                }
            }
        }

        // Follow trajectory
        if (aiData.trajectory.length > 0) {
            // Make sure that the worm follows the generated directory. If the worms turning speed is negative (switched key bindings), we need to take that in consideration when deciding where we should turn.
            var wanted;
            var turningSpeed = coreFunctions.getWormTurningSpeed(gameState, worm.id);
            // Reversed keys just flipped (either way): for a moment a skilled bot
            // presses the key it WOULD have pressed before the flip (see
            // PRECISION_CONFUSION). Bots without levels (legacy / preview) are
            // left alone.
            if (params.precision !== undefined && updateConfusion(aiData, turningSpeed, gameState.gameTime, params.precision)) {
                turningSpeed = -turningSpeed;
            }
            if ((aiData.trajectory[0].turningVelocity * turningSpeed) < 0) {
                wanted = constants.STEERING_LEFT;
            } else if (aiData.trajectory[0].turningVelocity * turningSpeed > 0) {
                wanted = constants.STEERING_RIGHT;
            } else {
                wanted = constants.STEERING_STRAIGHT;
            }
            if (params.steerDelay > 0 && !tron) {
                // Imprecise execution: a CHANGE of steering lands after a random
                // delay the planner knows nothing about (it overshoots, then
                // has to recover). Same steering as already applied: nothing to
                // wait for. No RNG is consumed at full precision.
                if (wanted !== aiData.appliedSteering) {
                    if (!aiData.pendingSteering || aiData.pendingSteering.steering !== wanted) {
                        aiData.pendingSteering = { steering: wanted, at: gameState.gameTime + random.random(gameState) * params.steerDelay };
                    }
                    if (gameState.gameTime >= aiData.pendingSteering.at) {
                        aiData.appliedSteering = wanted;
                        aiData.pendingSteering = null;
                    }
                } else {
                    aiData.pendingSteering = null;
                }
                gameStateFunctions.setPlayerSteering(gameState, player.id, aiData.appliedSteering === undefined ? wanted : aiData.appliedSteering);
            } else {
                aiData.appliedSteering = wanted;
                aiData.pendingSteering = null;
                gameStateFunctions.setPlayerSteering(gameState, player.id, wanted);
            }
        }
    }

    function getRandomTrajectory(gameState, worm, simDuration) {
        if (gameStateFunctions.isInStartPhase(gameState) || coreFunctions.getWormSpeed(gameState, worm.id) === 0) {
            return getRandomTwoStepTrajectory(gameState, worm, 0, coreFunctions.getWormTurningSpeed(gameState, worm.id), 50, 0, simDuration);
        } else if (gameStateFunctions.hasWormEffect(gameState, worm.id, "tronTurn")) {
            return random.randomElement(gameState, generateTronTurnTrajectories(gameState, worm, simDuration));
        } else {
            return getRandomTwoStepTrajectory(gameState, worm, coreFunctions.getWormSpeed(gameState, worm.id), coreFunctions.getWormTurningSpeed(gameState, worm.id), coreFunctions.getWormSpeed(gameState, worm.id), 0, simDuration);
        }
    }

    function getTrajectoryWithAngle(gameState, worm, angle, simDuration) {
        if (gameStateFunctions.isInStartPhase(gameState) || coreFunctions.getWormSpeed(gameState, worm.id) === 0) {
            return getTwoStepTrajectoryWithAngle(worm, angle, 0, coreFunctions.getWormTurningSpeed(gameState, worm.id), 50, 0, simDuration);
        } else if (gameStateFunctions.hasWormEffect(gameState, worm.id, "tronTurn")) {
            return random.randomElement(gameState, generateTronTurnTrajectories(gameState, worm, simDuration));
        } else {
            return getTwoStepTrajectoryWithAngle(worm, angle, coreFunctions.getWormSpeed(gameState, worm.id), coreFunctions.getWormTurningSpeed(gameState, worm.id), coreFunctions.getWormSpeed(gameState, worm.id), 0, simDuration);
        }
    }

    function getRandomTwoStepTrajectory(gameState, worm, speed1, turningSpeed1, speed2, turningSpeed2, simDuration) {
        var angle = random.random(gameState) * 2 * Math.PI - Math.PI;
        return getTwoStepTrajectoryWithAngle(worm, angle, speed1, turningSpeed1, speed2, turningSpeed2, simDuration);
    }

    function getTwoStepTrajectoryWithAngle(worm, angle, speed1, turningSpeed1, speed2, turningSpeed2, simDuration) {
        var turnDuration = Math.abs(angle / turningSpeed1);
        var steering = constants.STEERING_STRAIGHT;
        if (angle < 0) {
            steering = constants.STEERING_LEFT;
        } else if (angle > 0) {
            steering = constants.STEERING_RIGHT;
        }
        var trajectory = [];
        var x = worm.centerX;
        var y = worm.centerY;
        var direction = worm.direction;
        trajectory.push(trajectoryUtil.createTrajectory({
            startX: x,
            startY: y,
            startDirection: direction,
            duration: turnDuration,
            speed: speed1,
            turningVelocity: steering * turningSpeed1
        }));
        x = trajectory[0].endX;
        y = trajectory[0].endY;
        direction = trajectory[0].endDirection;
        trajectory.push(trajectoryUtil.createTrajectory({
            startX: x,
            startY: y,
            startDirection: direction,
            // Clamp at 0 so a sharp first turn that eats the whole lookahead (more
            // likely with a short, low-skill simDuration) can't make this negative.
            duration: Math.max(0, simDuration - turnDuration),
            speed: speed2,
            turningVelocity: steering * turningSpeed2
        }));
        return trajectory;
    }

    function generateTronTurnTrajectories(gameState, worm, simDuration) {
        var turnTime = 0.0001;
        var speed = coreFunctions.getWormSpeed(gameState, worm.id);
        var trajectories = [];

        [0, constants.STEERING_RIGHT, constants.STEERING_LEFT].forEach(function (steering) {
            var trajectory = [];
            // Turn on the spot
            trajectory.push(trajectoryUtil.createTrajectory({
                startX: worm.centerX,
                startY: worm.centerY,
                startDirection: worm.direction,
                duration: turnTime,
                speed: 0,
                turningVelocity: steering * Math.PI / 2 / turnTime
            }));
            // Go forward
            trajectory.push(trajectoryUtil.createTrajectory({
                startX: trajectory[0].endX,
                startY: trajectory[0].endY,
                startDirection: trajectory[0].endDirection,
                duration: simDuration,
                speed: speed,
                turningVelocity: 0
            }));
            trajectories.push(trajectory);
        });
        return trajectories;
    }

    // Build the per-sample trait scorer for this worm, or null when the persona
    // has no traits (or the board model is absent) - the null path costs nothing
    // and keeps legacy behaviour bit-identical.
    //
    // Score semantics (see evaluateTrajectory): survival is a hard ranking
    // constraint - a candidate that reaches the safe threshold beats any that
    // does not - and AMONG safe candidates the traits rank them: traitAvg is the
    // per-sample average of sum(weight_t * f_t(sample)), clamped to +-TRAIT_CLAMP,
    // with survival beyond the threshold only a light tiebreak. Among unsafe
    // candidates survival time tilted by the traits decides.
    var HUNT_RADIUS = 250;           // units: target-region attraction range
    var WALL_COMFORT = 30;           // units: the clearance a wall-hugger loves
    var OPPORTUNISM_CLEARANCE = 60;  // units: an opponent below this counts as boxed in
    // Only these traits are live: the rest of the catalog is parked (their
    // weights are ignored, the scoring code stays) until each is tuned in turn.
    var ACTIVE_TRAITS = { caution: true, aggression: true, space: true, planning: true, precision: true, greed: true };
    function traitWeight(traits, name) {
        return ACTIVE_TRAITS[name] ? (traits[name] || 0) : 0;
    }
    // The board's other worms within `reach` of ours, nearest first, at most
    // NEIGHBOUR_MAX of them (the only opponents a replan looks at).
    function nearbyOpponents(board, worm, reach) {
        var reach2 = reach * reach;
        var found = [];
        for (var i = 0; i < board.worms.length; i++) {
            var o = board.worms[i];
            if (o.id === worm.id) {continue;}
            var dx = o.x - worm.centerX, dy = o.y - worm.centerY;
            var d2 = dx * dx + dy * dy;
            if (d2 <= reach2) {found.push({ o: o, d2: d2 });}
        }
        found.sort(function (a, b) { return a.d2 - b.d2; });
        var out = [];
        for (var k = 0; k < found.length && k < NEIGHBOUR_MAX; k++) {out.push(found[k].o);}
        return out;
    }

    function makeScorer(gameState, aiData, worm, near, ownField, crowd) {
        var traits = aiData.traits;
        var board = aiData.board;
        if (!traits || !board) {return null;}
        var caution = traitWeight(traits, "caution");
        var aggression = traitWeight(traits, "aggression");
        var greed = traitWeight(traits, "greed");
        var wallAffinity = traitWeight(traits, "wallAffinity");
        var hunting = traitWeight(traits, "hunting");
        var opportunism = traitWeight(traits, "opportunism");
        var space = traitWeight(traits, "space");
        // (There was a `survival` trait that scaled how much staying alive is
        // worth. Removed: no bot should ever value living less, so survival
        // always counts in full and a stored value is ignored.)
        // Territory (the 1v1 level, crowd.territory): see the candidate term below.
        // Only with exactly one opponent on the board: it is a duel heuristic.
        var territory = crowd && crowd.territory ? crowd.territory : 0;
        var rival = null;
        if (territory) {
            for (var ri = 0; ri < board.worms.length; ri++) {
                var bw = board.worms[ri];
                if (bw.playerId === worm.playerId) {continue;}
                if (rival) { rival = null; break; } // more than one opponent worm: not a duel
                rival = bw;
            }
            if (!rival) {territory = 0;}
        }
        if (!caution && !aggression && !greed && !wallAffinity && !hunting && !opportunism && !space && !territory) {return null;}
        // Opponents once (not per sample): the nearby ones (see nearbyOpponents).
        var opponents = near || [];
        // The worm's own room map (its fresh trail not an obstacle) when the
        // ladder allows it; the shared map otherwise.
        var myIndex = -1;
        if (ownField !== false) {
            for (var i = 0; i < board.worms.length; i++) {
                if (board.worms[i].id === worm.id) {myIndex = i; break;}
            }
        }
        // Token attraction (greed): desirability by WHICH power-up it is (the
        // global table in powerup-values.js, 0 = ignored outright, 1 = full
        // pull), by who the effect targets, and a heavy discount when a rival
        // will clearly beat us there.
        var tokens = null;
        if (greed && board.powerUps.length) {
            tokens = [];
            var mySpeed = coreFunctions.getWormSpeed(gameState, worm.id) || 100;
            for (var pi = 0; pi < board.powerUps.length; pi++) {
                var pu = board.powerUps[pi];
                var worth = powerUpGreedValue(pu, gameState.powerUpGreedValues);
                if (!(worth > 0)) {continue;} // ignored: no pull, no aimed candidate, and nothing to avoid
                var desirability = worth * (pu.affects === "self" ? 1 : pu.affects === "others" ? 0.6 : 0.3);
                var myEta = Math.hypot(worm.centerX - pu.x, worm.centerY - pu.y) / mySpeed;
                if (pu.nearestWormIndex !== myIndex && pu.nearestEta < myEta * 0.8) {desirability *= 0.25;}
                tokens.push({ x: pu.x, y: pu.y, w: desirability, eta: myEta });
            }
        }
        // Boxed-in opponents (opportunism): precompute each opponent's own clearance.
        var boxed = null;
        if (opportunism && opponents.length) {
            boxed = [];
            for (var bi = 0; bi < opponents.length; bi++) {
                var oc = boardModel.clearanceAt(board, opponents[bi].x, opponents[bi].y);
                if (oc < OPPORTUNISM_CLEARANCE) {boxed.push({ o: opponents[bi], w: 1 - oc / OPPORTUNISM_CLEARANCE });}
            }
            if (!boxed.length) {boxed = null;}
        }
        // The hunt target: nearest opponent (the score leader needs match score,
        // which lives outside gameState; a persona-layer refinement).
        var target = null;
        if ((hunting || aggression) && opponents.length) {
            var nd0 = Infinity;
            for (var ti = 0; ti < opponents.length; ti++) {
                var tdx = opponents[ti].x - worm.centerX, tdy = opponents[ti].y - worm.centerY;
                var td = tdx * tdx + tdy * tdy;
                if (td < nd0) { nd0 = td; target = opponents[ti]; }
            }
        }
        var scorer = {};
        // Aggressors get a DIRECTED candidate (see the replan block) aimed at the
        // earliest point on any opponent's projected path that we can reach
        // CUT_MARGIN before they do: the intercept. Random / fan candidates
        // rarely line up a real cut-off by themselves. With no reachable
        // intercept it aims at the nearest head's ~1s projection instead.
        if (aggression > 0) {
            var mySpd = coreFunctions.getWormSpeed(gameState, worm.id) || 100;
            var myTurn = Math.abs(coreFunctions.getWormTurningSpeed(gameState, worm.id)) || 1;
            var bestP = 0, bestAngle;
            for (var oi = 0; oi < opponents.length; oi++) {
                var opath = opponents[oi].path;
                if (!opath) {continue;}
                for (var pi = 0; pi < opath.n; pi++) {
                    var st = (pi + 1) * opath.step;
                    var pp = threatProbability(st);
                    if (pp <= bestP) {break;} // probability only falls along the path
                    var ddx = opath.xs[pi] - worm.centerX, ddy = opath.ys[pi] - worm.centerY;
                    var ang = Math.atan2(ddy, ddx) - worm.direction;
                    while (ang > Math.PI) {ang -= 2 * Math.PI;}
                    while (ang < -Math.PI) {ang += 2 * Math.PI;}
                    var eta = Math.abs(ang) / myTurn + Math.sqrt(ddx * ddx + ddy * ddy) / mySpd;
                    if (eta + CUT_MARGIN <= st) { bestP = pp; bestAngle = ang; break; }
                }
            }
            if (bestAngle !== undefined) {scorer.targetAngle = bestAngle;}
        }
        if (scorer.targetAngle === undefined && (aggression > 0 || hunting > 0) && target) {
            // No reachable intercept: approach the nearest head instead.
            var aim = projectedAt(target, 1);
            var want = Math.atan2(aim.y - worm.centerY, aim.x - worm.centerX) - worm.direction;
            while (want > Math.PI) {want -= 2 * Math.PI;}
            while (want < -Math.PI) {want += 2 * Math.PI;}
            scorer.targetAngle = want;
        }
        // Greedy personas get a token-directed candidate: same lesson as the
        // cut-off candidate - random paths rarely pass within the attraction
        // radius of a token, so without an aimed option greed has nothing to pick.
        if (greed > 0 && tokens) {
            var bestTok = null, bestVal = 0;
            for (var bt = 0; bt < tokens.length; bt++) {
                var val = tokens[bt].w / (1 + tokens[bt].eta);
                if (val > bestVal) { bestVal = val; bestTok = tokens[bt]; }
            }
            if (bestTok) {
                var wantTok = Math.atan2(bestTok.y - worm.centerY, bestTok.x - worm.centerX) - worm.direction;
                while (wantTok > Math.PI) {wantTok -= 2 * Math.PI;}
                while (wantTok < -Math.PI) {wantTok += 2 * Math.PI;}
                scorer.tokenAngle = wantTok;
            }
        }
        // Per-candidate terms (added to the sample average): begin() resets any
        // per-candidate accumulators before the samples, candidate(trajectory)
        // returns the term afterwards.
        scorer.begins = [];
        scorer.candidates = [];
        // Aggression > 0: the cut-off. For each opponent, the best sample on the
        // candidate that lands on their projected path (within AGGRESSION_RADIUS)
        // and gets there at least CUT_MARGIN before they do: our trail will be in
        // their way, with the same probability the survival check uses for them
        // going there. Counted once per opponent (best cut), best opponent wins;
        // a full cut is worth CUT_WEIGHT, more than any other consideration.
        var cutBest = null;
        if (aggression > 0 && opponents.length) {
            cutBest = new Float32Array(opponents.length);
            scorer.begins.push(function () { for (var c = 0; c < cutBest.length; c++) {cutBest[c] = 0;} });
            scorer.candidates.push(function () {
                var top = 0;
                for (var c = 0; c < cutBest.length; c++) {if (cutBest[c] > top) {top = cutBest[c];}}
                return aggression * CUT_WEIGHT * top;
            });
        }
        // Space: the board model's space-ahead fields (one per cardinal
        // direction, blended by heading) read ONCE per candidate, at its end
        // sample in its end heading: how much room is left in the future if we
        // keep going this way. Space along the curve is irrelevant as long as
        // we survive it. Positive seeks room ahead, negative seeks dead ends.
        var spc = null;
        if (space) {
            spc = { lastX: worm.centerX, lastY: worm.centerY, prevX: worm.centerX, prevY: worm.centerY };
            scorer.begins.push(function () { spc.lastX = spc.prevX = worm.centerX; spc.lastY = spc.prevY = worm.centerY; });
            scorer.candidates.push(function () {
                return space * 0.5 * boardModel.spaceAheadAt(board, spc.lastX, spc.lastY, spc.lastX - spc.prevX, spc.lastY - spc.prevY);
            });
        }
        // Territory: at the candidate's end, the share of the board we reach
        // before the rival (board-model territoryShare), against where the rival
        // will be by then (their projection nearest that time). Centred: an even
        // split adds nothing, owning more of the board pulls, less pushes away.
        // Survival still comes first: this only ranks the safe candidates.
        // Measured 2026-10-01 (tools/bots/duel.mjs, the duel arenas): weight 1
        // +7 Elo (95% -4..+18) over 4000 games, for twice the bot CPU; no
        // level sets it. A 1v1 there lasts ~14s (rounds-stats.mjs): mostly
        // not crashing early, little time for territory to pay off. Blocking
        // the candidate's own trail in the flood did no better (-3). Worth a
        // look again on bigger arenas.
        var ter = null;
        if (territory) {
            ter = { x: worm.centerX, y: worm.centerY, t: 0 };
            scorer.begins.push(function () { ter.x = worm.centerX; ter.y = worm.centerY; ter.t = 0; });
            scorer.candidates.push(function () {
                var pr = rival.projected, best = null;
                for (var pj = 0; pj < pr.length; pj++) {
                    if (!best || Math.abs(pr[pj].t - ter.t) < Math.abs(best.t - ter.t)) {best = pr[pj];}
                }
                var ox = best && ter.t > 0.25 ? best.x : rival.x, oy = best && ter.t > 0.25 ? best.y : rival.y;
                return territory * (boardModel.territoryShare(board, ter.x, ter.y, ox, oy) - 0.5);
            });
        }
        scorer.sample = function (x, y, t) {
            var s = 0;
            if (ter) { ter.x = x; ter.y = y; ter.t = t; }
            if (caution) {
                // Signed, centered: open (clearance at/above the reference) reads
                // +0.5, right against an obstacle -0.5, scaled by the weight. Positive
                // caution seeks room; negative is a daredevil that likes skimming
                // walls and trails as long as the path still survives.
                // Our own field: our head / immune trail is not an obstacle to us.
                var clear = boardModel.clearanceAt(board, x, y, myIndex);
                s += caution * (Math.min(1, clear / CAUTION_CLEARANCE_REF) - 0.5);
            }
            if (cutBest) {
                // Cut-off accumulation: points on each opponent's projected path
                // that they reach later than t (ramping to full at t + CUT_MARGIN).
                for (var o = 0; o < opponents.length; o++) {
                    var cp = opponents[o].path;
                    if (!cp) {continue;}
                    var first = Math.floor(t / cp.step); // first point they reach after us
                    var cgap = skipGap(cp);
                    for (var ci = first; ci < cp.n; ci++) {
                        var cs = (ci + 1) * cp.step;
                        var cprob = threatProbability(cs);
                        if (cprob <= cutBest[o]) {break;} // can't beat the best any more
                        var cdx = cp.xs[ci] - x, cdy = cp.ys[ci] - y;
                        var cd = Math.sqrt(cdx * cdx + cdy * cdy);
                        if (cd >= AGGRESSION_RADIUS) {
                            // Out of reach, and so are the next ceil((cd - R) / gap) - 1
                            // points (consecutive points are at most `gap` apart): skip
                            // them. Probability only falls further along, so the break
                            // above ends the scan exactly where it would have.
                            var cjump = cgap > 0 ? Math.ceil((cd - AGGRESSION_RADIUS) / cgap) : 1;
                            if (cjump > 1) {ci += cjump - 1;}
                            continue;
                        }
                        var lead = Math.min(1, (cs - t) / CUT_MARGIN);
                        var cut = cprob * lead * (1 - cd / AGGRESSION_RADIUS);
                        if (cut > cutBest[o]) {cutBest[o] = cut;}
                    }
                }
            }
            if (aggression && opponents.length) {
                // Head proximity: a bonus for being in the BAND around a point in
                // FRONT of any opponent (where their head is projected to be
                // HEAD_LEAD later; see HEAD_TOO_CLOSE .. HEAD_RADIUS): general
                // proximity ahead of them, not hugging.
                // A cut (above) is worth more, so cutting in front stays the
                // preferred move. Negative aggression is this term reversed.
                var near = 0, nearAhead = 0.5;
                for (var ao = 0; ao < opponents.length; ao++) {
                    var ap = projectedAt(opponents[ao], t + HEAD_LEAD); // a point in front of them
                    var adx = x - ap.x, ady = y - ap.y;
                    var ad = Math.sqrt(adx * adx + ady * ady);
                    var ak = ad <= HEAD_TOO_CLOSE ? 0
                        : ad < HEAD_NEAR ? (ad - HEAD_TOO_CLOSE) / (HEAD_NEAR - HEAD_TOO_CLOSE)
                        : ad <= HEAD_FAR ? 1
                        : ad < HEAD_RADIUS ? 1 - (ad - HEAD_FAR) / (HEAD_RADIUS - HEAD_FAR) : 0;
                    if (ak > near) {
                        near = ak;
                        // Ahead of their heading (1) .. behind them (0): in front is
                        // where a cut-off lands, behind is where their fresh trail is.
                        var ahd = ad > 1e-6 ? (adx * Math.cos(opponents[ao].direction) + ady * Math.sin(opponents[ao].direction)) / ad : 0;
                        nearAhead = 0.5 + 0.5 * ahd;
                    }
                }
                // Full weight in front of them, half behind.
                s += aggression * HEAD_WEIGHT * near * (0.5 + 0.5 * nearAhead);
            }
            if (tokens) {
                // Token attraction: bonus for passing near a desirable power-up
                // (negative greed: a penalty, it steers clear of them).
                for (var g = 0; g < tokens.length; g++) {
                    var gd = Math.hypot(tokens[g].x - x, tokens[g].y - y);
                    if (gd < GREED_RADIUS) {s += greed * 0.5 * tokens[g].w * (1 - gd / GREED_RADIUS);}
                }
            }
            if (spc) {
                // Track the candidate's last two samples (end position + heading).
                spc.prevX = spc.lastX; spc.prevY = spc.lastY;
                spc.lastX = x; spc.lastY = y;
            }
            if (wallAffinity) {
                // Comfort band around WALL_COMFORT clearance: a positive weight is a
                // wall-hugger, a negative one keeps to the middle.
                var wc = boardModel.clearanceAt(board, x, y);
                var band = 1 - Math.min(2, Math.abs(wc - WALL_COMFORT) / WALL_COMFORT);
                s += wallAffinity * band;
            }
            if (hunting && target) {
                // Pull toward the target's region (wider and softer than the
                // aggression cut-off kernel).
                var hd = Math.hypot(target.x - x, target.y - y);
                if (hd < HUNT_RADIUS) {s += hunting * (1 - hd / HUNT_RADIUS) * 0.6;}
            }
            if (boxed) {
                // Move on opponents who are already hemmed in - finish them.
                for (var bx = 0; bx < boxed.length; bx++) {
                    var bp = projectedAt(boxed[bx].o, t);
                    var bd = Math.hypot(bp.x - x, bp.y - y);
                    if (bd < HUNT_RADIUS) {s += opportunism * boxed[bx].w * (1 - bd / HUNT_RADIUS) * 0.6;}
                }
            }
            return s;
        };
        return scorer;
    }

    // An opponent's projected position at time t, interpolated between the board
    // model's fixed projection anchors (current position at t=0).
    function projectedAt(entry, t) {
        var prev = entry, prevT = 0;
        for (var i = 0; i < entry.projected.length; i++) {
            var pr = entry.projected[i];
            if (t <= pr.t) {
                var f = (t - prevT) / (pr.t - prevT);
                return { x: prev.x + (pr.x - prev.x) * f, y: prev.y + (pr.y - prev.y) * f };
            }
            prev = pr;
            prevT = pr.t;
        }
        return entry.projected.length ? entry.projected[entry.projected.length - 1] : entry;
    }

    // The opponents' projected trails as threats for checkTrajectory, or null
    // when there is no board model (legacy / cheap paths: unchanged behaviour).
    // Each threat: the dense projected path plus the centre distance at which a
    // sample on our path touches it (both radii, plus half the point spacing so
    // the polyline sampling can't leave a gap).
    function makeThreats(aiData, worm, near) {
        var board = aiData.board;
        if (!board) {return null;}
        var myRadius = worm.radius || 3;
        var threats = null;
        var list = near || [];
        for (var i = 0; i < list.length; i++) {
            var o = list[i];
            if (!o.path || o.path.n === 0) {continue;}
            if (!threats) {threats = [];}
            threats.push({ path: o.path, hitDist: myRadius + o.radius + o.path.step * o.speed * 0.5 });
        }
        return threats;
    }

    // Survival time + trait score for one candidate. With no scorer the score IS
    // the survival time, so all comparisons reduce to the legacy behaviour.
    function evaluateTrajectory(gameState, worm, trajectory, simDelta, scorer, threats, safeTime) {
        if (!scorer) {
            var t = checkTrajectory(gameState, worm, trajectory, simDelta, null, threats);
            return { time: t, score: t };
        }
        var traitSum = 0;
        var samples = 0;
        for (var bi = 0; bi < scorer.begins.length; bi++) {scorer.begins[bi]();}
        // Trait samples carry the same survival weight as the time they sit in:
        // a stylish stretch we probably never live to drive counts for less.
        var time = checkTrajectory(gameState, worm, trajectory, simDelta, function (x, y, at, weight) {
            traitSum += scorer.sample(x, y, at) * weight;
            samples += weight;
        }, threats);
        var traitAvg = samples > 0 ? traitSum / samples : 0;
        for (var ci2 = 0; ci2 < scorer.candidates.length; ci2++) {traitAvg += scorer.candidates[ci2](trajectory, time, safeTime);}
        if (traitAvg > TRAIT_CLAMP) {traitAvg = TRAIT_CLAMP;}
        if (traitAvg < -TRAIT_CLAMP) {traitAvg = -TRAIT_CLAMP;}
        // score = survival * S + T. S is the survival value: safe candidates
        // (reaching safeTime) start at 2 * safeTime, above any unsafe one's
        // (its time), with survival beyond the threshold a light tiebreak. T is
        // the trait value, the trait average on the same scale (safeTime for
        // safe candidates, time for unsafe). A safe candidate always outranks
        // an unsafe one, and the traits rank the safe ones.
        var safe = safeTime !== undefined && time >= safeTime;
        var S = safe ? 2 * safeTime + EXTRA_SURVIVAL_WEIGHT * (time - safeTime) : time;
        var T = (safe ? safeTime : time) * traitAvg;
        var score = S + T;
        return { time: time, score: score };
    }

    // Expected survival time of a candidate path. Certain obstacles (walls and
    // trail already on the grid) end the path outright. Opponents' projected
    // trails (`threats`) are probable obstacles: reaching a spot BEFORE the
    // opponent does is free (they hit us, or our trail); reaching it after them
    // means collision with the probability of that projection holding. Each
    // opponent contributes once, at the highest probability found so far along
    // the path, and from that point on the remaining survival time only counts
    // for (1 - p) of its value; independent opponents multiply.
    function checkTrajectory(gameState, worm, trajectory, simDelta, onSample, threats) {
        // Coarser stepping (larger simDelta) trades collision precision for speed;
        // defaults to the standard step when omitted (real matches).
        simDelta = simDelta === undefined ? SIMULATION_DELTA : simDelta;
        var playArea = gameState.playArea;

        var wormRadius = coreFunctions.getWormRadius(gameState, worm.id);
        var segmentTime = 0;
        var time = 0;
        var trajectoryIndex = 0;
        var distanceTravelled = 0;
        var immunityDistance = constants.IMMUNITY_DISTANCE_MULTIPLIER * worm.radius;
        var weight = 1;       // survival weight: product over opponents of (1 - maxP)
        var maxP = null;      // per threat: highest collision probability so far
        if (threats && threats.length) {
            maxP = new Float32Array(threats.length);
        }
        var simTime = 0;      // unweighted path time (for the threat lookups)
        // Hoisted out of the step loop: neither changes along a candidate.
        var wallHackEffect = gameStateFunctions.getWormEffect(gameState, worm.id, "wallHack");
        var wallHack = wallHackEffect !== null;
        // A wall hack is only good until it runs out, and a worm through the wall
        // comes back in on the far side: the path is checked there, not out in
        // the void past the wall (see wallHackWrap).
        var wallHackLeft = wallHack && wallHackWrap ? wallHackEffect.timeLeft : Infinity;
        var mapShape = gameState.map.shape;
        var centreOnly = {centerX: 0, centerY: 0, radius: 0};
        var grid = playArea.grid, gridCols = playArea.cols, gridRows = playArea.rows;
        var wormId = worm.id;
        var fromCells = worm.distanceTravelledFromCells;
        var stampMap = fromCells instanceof CellStamps ? fromCells : null;
        var baseDistance = worm.distanceTravelled;
        var r2 = wormRadius * wormRadius;
        // Exact coarse summary of the grid: a block with no painted cell can't hold an
        // obstacle, so a step whose head only covers empty blocks skips the cell reads.
        var blocks = getGridBlocks(playArea);
        var blockCounts = blocks.counts, bcols = blocks.bcols;
        var position = {x: 0, y: 0}; // this step's head position, rewritten each step
        var headCircle = {centerX: 0, centerY: 0, radius: wormRadius}; // for the map test, likewise
        while (trajectoryIndex < trajectory.length) {
            segmentTime += simDelta;
            distanceTravelled += simDelta * trajectory[trajectoryIndex].speed;
            while (trajectoryIndex < trajectory.length - 1 && segmentTime > trajectory[trajectoryIndex].duration) {
                segmentTime -= trajectory[trajectoryIndex].duration;
                trajectoryIndex++;
            }
            trajectoryUtil.followTrajectoryInto(trajectory[trajectoryIndex], segmentTime, position);
            if (segmentTime >= trajectory[trajectoryIndex].duration) {
                segmentTime -= trajectory[trajectoryIndex].duration;
                trajectoryIndex++;
            }

            var collision = false;
            var hacking = wallHack && simTime < wallHackLeft;
            if (hacking && wallHackWrap) {
                centreOnly.centerX = position.x;
                centreOnly.centerY = position.y;
                if (!shapeSpatialRelations.contains(mapShape, centreOnly)) {
                    if (mapShape.type === "rectangle") {
                        if (position.x < mapShape.x) {position.x += mapShape.width;} else if (position.x > mapShape.maxX) {position.x -= mapShape.width;}
                        if (position.y < mapShape.y) {position.y += mapShape.height;} else if (position.y > mapShape.maxY) {position.y -= mapShape.height;}
                    } else if (mapShape.type === "stadium") {
                        var mirrored = stadiumMirror(mapShape, position.x, position.y, 0);
                        position.x = mirrored.x;
                        position.y = mirrored.y;
                    }
                }
            }
            // Map collision detection
            headCircle.centerX = position.x;
            headCircle.centerY = position.y;
            if (!hacking && !shapeSpatialRelations.contains(mapShape, headCircle)) {
                collision = true;
            }
            if (!collision) {
                // Worm collision detection: the head circle's grid cells, walked in
                // place (the same INTERSECTION rounding shapeToGridConverter uses for
                // a worm, without building the cell list), stopping at the first hit.
                var px = position.x, py = position.y;
                var firstRow = Math.max(0, Math.floor(py - wormRadius));
                var lastRow = Math.min(gridRows - 1, Math.ceil(py + wormRadius));
                var midRow = Math.round(py);
                var travelled = baseDistance + distanceTravelled;
                var anyPainted = false;
                var bc0 = Math.max(0, Math.floor(px - wormRadius)) >> BLOCK_SHIFT;
                var bc1 = Math.min(gridCols - 1, Math.ceil(px + wormRadius)) >> BLOCK_SHIFT;
                for (var br = firstRow >> BLOCK_SHIFT; br <= lastRow >> BLOCK_SHIFT && !anyPainted; br++) {
                    for (var bc = bc0; bc <= bc1; bc++) {
                        if (blockCounts[br * bcols + bc] !== 0) { anyPainted = true; break; }
                    }
                }
                for (var row = firstRow; anyPainted && row <= lastRow && !collision; row++) {
                    var dy = midRow - row;
                    var dx = Math.sqrt(r2 - dy * dy);
                    var firstCol = Math.max(0, Math.floor(px - dx));
                    var lastCol = Math.min(gridCols - 1, Math.ceil(px + dx));
                    var rowBase = row * gridCols;
                    for (var col = firstCol; col <= lastCol; col++) {
                        var value = grid[rowBase + col];
                        if (value !== constants.PLAY_AREA_FREE) {
                            var cell = rowBase + col;
                            if (value.indexOf(wormId) === -1 //TODO Needs to get improved. Or just refactor when we dont have playArea left
                                || travelled - (stampMap ? stampMap.get(cell) : fromCells[cell]) > immunityDistance) {
                                collision = true;
                                break;
                            }
                        }
                    }
                }
            }
            if (collision) {
                return time;
            }
            simTime += simDelta;
            if (maxP) {
                var changed = false;
                for (var ti = 0; ti < threats.length; ti++) {
                    if (maxP[ti] >= 1) {continue;}
                    var path = threats[ti].path;
                    var hd = threats[ti].hitDist;
                    var hd2 = hd * hd;
                    // Only points the opponent reaches no later than we do (index
                    // i is at time (i + 1) * step). Probability falls with the
                    // point's time, so the first hit is the most probable one.
                    var last = Math.min(path.n, Math.floor(simTime / path.step)) - 1;
                    var gap = skipGap(path);
                    for (var pi = 0; pi <= last;) {
                        var pdx = path.xs[pi] - position.x, pdy = path.ys[pi] - position.y;
                        var pd2 = pdx * pdx + pdy * pdy;
                        if (pd2 <= hd2) {
                            var prob = threatProbability((pi + 1) * path.step);
                            if (prob > maxP[ti]) { maxP[ti] = prob; changed = true; }
                            break;
                        }
                        // Consecutive points are at most `gap` apart, so none of the next
                        // ceil((d - hd) / gap) - 1 can be within hd of us: jump past them.
                        var jump = gap > 0 ? Math.ceil((Math.sqrt(pd2) - hd) / gap) : 1;
                        pi += jump > 1 ? jump : 1;
                    }
                }
                if (changed) {
                    weight = 1;
                    for (var wi = 0; wi < threats.length; wi++) {weight *= 1 - maxP[wi];}
                }
            }
            time += simDelta * weight;
            if (onSample) {onSample(position.x, position.y, simTime, weight);}
        }
        return time;
    }

    return {
        update: update
    };
};
